import React, { useState, useEffect } from 'react';
import { inventoryItemsAPI, InventoryItem } from '../../services/api';
import { PlusIcon, ArchiveBoxIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import InventoryItemForm from './InventoryItemForm';
import InventoryItemDetail from './InventoryItemDetail';
import { Card, CardBody, Button, Badge, ConfirmModal, Spinner, EmptyState } from '../ui';

const InventoryItems: React.FC = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await inventoryItemsAPI.getAll();
      setItems(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch inventory items');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleViewDetails = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsDetailOpen(true);
  };

  const handleEdit = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsDetailOpen(false);
    setIsFormOpen(true);
  };

  const handleDeleteRequest = (item: InventoryItem) => {
    setDeleteTarget(item);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      setError('');
      await inventoryItemsAPI.delete(deleteTarget.id);
      setSuccess('Inventory item deleted successfully');
      setDeleteTarget(null);
      if (selectedItem?.id === deleteTarget.id) {
        setSelectedItem(null);
        setIsDetailOpen(false);
      }
      await fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete inventory item');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setError('');
    setSuccess('Inventory item saved successfully');
    fetchItems();
    setTimeout(() => setSuccess(''), 3000);
  };

  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
    switch (status) {
      case 'IN_STOCK':
        return 'success';
      case 'LOW_STOCK':
        return 'warning';
      case 'OUT_OF_STOCK':
        return 'error';
      case 'RESERVED':
        return 'primary';
      case 'DAMAGED':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Inventory Items</h2>
          <p className="mt-1 text-sm text-neutral-600">Track items in warehouse locations</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAdd}
          leftIcon={<PlusIcon className="h-5 w-5" />}
        >
          Add Item
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-success-50 border border-success-200 text-success-600 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="space-y-3 md:hidden">
        {items.map((item) => (
          <Card key={item.id} variant="elevated">
            <CardBody className="p-4">
              <button type="button" onClick={() => handleViewDetails(item)} className="block w-full text-left">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{item.product?.name || 'Unknown'}</p>
                    <p className="mt-1 text-xs text-neutral-500">{item.lot?.lotNumber || item.batchNumber || 'No lot'}</p>
                  </div>
                  <Badge variant={getStatusVariant(item.status)}>
                    {item.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Quantity</p>
                    <p className="mt-1 font-semibold text-neutral-900">{item.quantity} {item.unit || item.product?.unit || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Location</p>
                    <p className="mt-1 text-neutral-700">
                      {item.tray
                        ? `${item.tray.bin?.shelf?.warehouse?.name} - ${item.tray.bin?.shelf?.code} - ${item.tray.bin?.code} - ${item.tray.code}`
                        : 'Unassigned'}
                    </p>
                  </div>
                </div>
              </button>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-neutral-100 pt-3">
                <button type="button" onClick={() => handleViewDetails(item)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" aria-label={`View inventory item ${item.id}`}><EyeIcon className="h-4 w-4" />View</button>
                <button type="button" onClick={() => handleEdit(item)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-primary-200 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-50" aria-label={`Edit inventory item ${item.id}`}><PencilIcon className="h-4 w-4" />Edit</button>
                <button type="button" onClick={() => handleDeleteRequest(item)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-error-200 px-3 py-2 text-xs font-semibold text-error-700 hover:bg-error-50" aria-label={`Delete inventory item ${item.id}`}><TrashIcon className="h-4 w-4" />Delete</button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <Card variant="elevated" className="hidden md:block">
        <CardBody className="p-0">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Lot Number
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => handleViewDetails(item)}
                  className="hover:bg-neutral-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                    {item.product?.name || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                    {item.tray
                      ? `${item.tray.bin?.shelf?.warehouse?.name} - ${item.tray.bin?.shelf?.code} - ${item.tray.bin?.code} - ${item.tray.code}`
                      : 'Unassigned'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {item.quantity} {item.unit || item.product?.unit || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={getStatusVariant(item.status)}>
                      {item.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                    {item.lot?.lotNumber || item.batchNumber || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleViewDetails(item); }} className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900" title="View inventory item" aria-label={`View inventory item ${item.id}`}><EyeIcon className="h-5 w-5" /></button>
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleEdit(item); }} className="rounded-lg p-2 text-primary-600 hover:bg-primary-50 hover:text-primary-900" title="Edit inventory item" aria-label={`Edit inventory item ${item.id}`}><PencilIcon className="h-5 w-5" /></button>
                      <button type="button" onClick={(event) => { event.stopPropagation(); handleDeleteRequest(item); }} className="rounded-lg p-2 text-error-600 hover:bg-error-50 hover:text-error-900" title="Delete inventory item" aria-label={`Delete inventory item ${item.id}`}><TrashIcon className="h-5 w-5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {items.length === 0 && (
        <EmptyState
          icon={<ArchiveBoxIcon className="h-12 w-12" />}
          title="No inventory items"
          description="Get started by adding inventory items."
        />
      )}

      <InventoryItemForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={handleFormSuccess}
        item={selectedItem}
      />

      <InventoryItemDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        item={selectedItem}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
      />

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete inventory item?"
        message={deleteTarget ? `${deleteTarget.product?.name || 'Inventory item'} | ${deleteTarget.quantity} ${deleteTarget.unit || deleteTarget.product?.unit || 'units'} | Lot ${deleteTarget.lot?.lotNumber || deleteTarget.batchNumber || 'none'}. This permanently removes this stock row.` : ''}
        confirmText="Delete permanently"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default InventoryItems;
